import React from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import { makeStyles, useTheme } from '@mui/styles';
import { Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import CustomButton from 'components/CustomButton';
import { InterviewerCard } from './MiddleSection';
import Toggle from 'components/Toggle';
import useCodingStore from './CodingStore';
import { getRandomAudioCue, InterviewerStates } from './constant';
import WavRecorder from 'utils/WavRecorder';
import MessageIndicator from 'components/MessageIndicator';
import { generateHint } from 'services';

const useStyles = makeStyles((theme) => ({
  container : {
    height : '100%',
    overflow : 'auto',
    display : 'flex',
    flexDirection : 'column',
    gap : theme.spacing(5)
  },
  header : {
    display : 'flex',
    justifyContent : 'space-between',
    borderBottom : `1px solid`,
    borderColor: theme.palette.neutral['clr-400'],
    paddingBottom : theme.spacing(3)
  },
  chatSection : {
    height : '100%',
    overflow : 'auto',
    display : 'flex',
    flexDirection : 'column',
    gap : theme.spacing(5)
  },
  footer : {
    display : 'flex',
    flexDirection : 'column',
    gap : theme.spacing(3),
    alignItems : 'center'
  },
  card : {
    width : '75%',
    borderRadius : theme.spacing(2),
    border : '1px solid',
    borderColor : theme.palette.neutral['clr-200'],
    padding : theme.spacing(3),
    display : 'flex',
    flexDirection : 'column',
    gap : theme.spacing(2)
  },
  divider : {
    height : '0.5px',
    width : '100%',
    backgroundColor :  theme.palette.neutral['clr-200'] 
  },
  cardContainer: {
    display : 'flex',
    justifyContent : 'flex-end',
    width : '100%',
  }
}));

const IntervieweeCard = ({ loading=false, data={} }) => {
  const classes = useStyles();

  return loading ? 
    <Box className={classes.cardContainer}>
      <MessageIndicator position='right'/> 
    </Box>
    :
    <Box className={classes.cardContainer}>
      <Box className={classes.card}>
        <Typography variant='body01-bold' color='primary.main'>You</Typography>
        <Box className={classes.divider}/>
        <Typography variant='body01-semiBold'>
          {data?.question}
        </Typography>
      </Box>
    </Box>
}

const Hint = ({ open, onClose }) => {
  const theme = useTheme();
  const classes = useStyles();

  const audioRef = React.useRef(null);
  const wavRecorder = React.useRef(null);
  const recordingTimeoutRef = React.useRef(null);
  const countdownIntervalRef = React.useRef(null);
  const [countdown, setCountdown] = React.useState(30);
  const [activeHint, setActiveHint] = React.useState(0);
  const [tempHints, setTempHints] = React.useState([]);
  const [recordingStarted, setRecordingStarted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const [
    setInterviewerState, interviewerState, setHints, hints,
    attempt, analysis, questionType, challenge
  ] = useCodingStore(state => [
    state.setInterviewerState, state.interviewerState, state.setHints, state.hints,
    state.attempt, state.analysis, state.questionType, state.challenge
  ]);

  const handleClose = () => {
    if(!recordingStarted) {
      setInterviewerState(InterviewerStates.LISTINING);
      onClose();
    }
  }

  React.useEffect(()=>{
    setTempHints(hints);
  }, [open, hints]);

  React.useEffect(()=>{
    const cue = getRandomAudioCue("Hint_Intro_Cues")
    if(hints.length ===2) return;
    
    setTempHints([
      ...hints,
      {
        intro: cue.text,
        question: '',
        answer: '',
      },
    ]);

    toSpeech(cue.audioUrl);
    setInterviewerState(InterviewerStates.SPEAKING);
  },[open]);

  React.useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
  
    const handleAudioEnd = () => {
      setInterviewerState(InterviewerStates.LISTINING);
    };
  
    audio.addEventListener('ended', handleAudioEnd);
  
    return () => {
      audio.removeEventListener('ended', handleAudioEnd);
    };
  }, [audioRef]);

  const toSpeech = React.useCallback(async (audioUrl='') => {
    try {
      const audio = audioRef.current;
      if (!audio) throw new Error("Audio element not found");

      audio.onerror = null;

      const playPromise = new Promise((resolve, reject) => {
        audio.onerror = () => {
          reject(new Error("Audio failed to load"));
        };
        audio.onloadeddata = () => {
          audio.onerror = null;
          resolve();
        };
      });

      audio.src = audioUrl;
      await playPromise;
      await audio.play();
    } catch (error) {
      console.log(error)
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current.dispatchEvent(new Event('ended'));
    }
  }, []);

  const handleRecordedAudio = React.useCallback(async (audioBlob) => {
    const data = {
      sourceCode: analysis[questionType].sourceCode,
      codeChallengeAttemptId: attempt._id,
    }

    try {
      const handleEvent = (event, data) => {
        if(event==='progress'){
          setLoading(true);
        }else if(event==='analysed'){
          if(activeHint === 0){
            setHints([{intro: tempHints[0]?.intro, question: data?.query, answer: data?.hint}]);
          }
          if(activeHint === 1){
            setHints([
              tempHints[0],
              {intro: tempHints[1]?.intro, question: data?.query, answer: data?.hint}
            ]);
          }

          toSpeech(getRandomAudioCue("Hint_Response_Cues").audioUrl);
          setLoading(false);
        }
      };

      generateHint({id: challenge._id, data, audioBlob}, handleEvent);
    } catch (error) {
      console.error(error);
    }
  }, [tempHints, activeHint, challenge, attempt, toSpeech, analysis, questionType]);

  const startRecording = () => {
    wavRecorder.current = new WavRecorder();
    wavRecorder.current.start();
    setRecordingStarted(true);
    setCountdown(30);

    recordingTimeoutRef.current = setTimeout(() => {
      endRecording();
    }, 30000);
    
    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(!countdownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }
  
  const endRecording = () => {
    wavRecorder.current?.stop(handleRecordedAudio);
    wavRecorder.current = null;
    setRecordingStarted(false);

    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
    }
  }

  React.useEffect(()=>{
    setActiveHint(tempHints.length - 1);
  }, [tempHints]);

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={handleClose}
      hideBackdrop
      variant="persistent"
      PaperProps={{
        sx: {
          width: 548,
          top: '76px',
          height: 'calc(100vh - 76px)',
          position: 'fixed',
          backdropFilter: 'blur(50px)',
          boxShadow: '4px 0 24px 0 #00000040',
          borderRight: '1px solid',
          borderColor: theme.palette.neutral['clr-400'],
          padding : theme.spacing(6,10)
        },
      }}
    >
      <Box className={classes.container}>
        <audio ref={audioRef}></audio>
        <Box className={classes.header}>
          <Typography variant='h6-semiBold' color='primary.main'>
            Ask your Interviewer
          </Typography>
          <Close 
            color='#636F7F' 
            onClick={handleClose} 
            sx={{cursor : 'pointer'}}
          />
        </Box>
        <Box className={classes.chatSection}>
          {
            tempHints.length === 1 &&
              <Box style={{ display: 'flex', gap: 8, padding: '8px', borderRadius: '12px', backgroundColor: '#F5F9FF' }}>
                <InfoOutlinedIcon style={{ fontSize: '20px', marginTop: '4px', color: '#003E8C' }}/>
                <Typography variant='body01-semiBold' color='primary.main'>
                  Interviewer answers only around the problem statement. Use hints to ask relevant doubts.
                </Typography>
              </Box>
          }
          {
            tempHints.length > 1 &&
            <Toggle
              width="100%"
              height={48}
              options={[{id: 0, label: 'Hint 1'}, {id: 1, label: 'Hint 2'}]}
              value={activeHint}
              onChange={(e)=> setActiveHint(e.id)}
            />
          }
          {
            tempHints[activeHint]?.intro && 
              <InterviewerCard data={{question: tempHints[activeHint]?.intro}} />
          }
          {
            (tempHints[activeHint]?.question || tempHints[activeHint]?.answer || loading) && 
              <IntervieweeCard
                loading={loading}
                data={{
                  question: tempHints[activeHint]?.question?.length > 0 
                    ? tempHints[activeHint]?.question
                    : "Not recorded properly",
                }}
              />
          }
          {
            tempHints[activeHint]?.answer &&
              <InterviewerCard
                data={{question: tempHints[activeHint]?.answer}}
                loading={recordingStarted}
              />
          }
        </Box>
        <Box className={classes.footer}>
          <CustomButton
            size='large'
            variant={recordingStarted ? 'outlined' : 'contained'}
            sx={{width : "100%"}}
            onClick={()=> recordingStarted ? endRecording() : startRecording()}
            disabled={
              interviewerState === InterviewerStates.SPEAKING ||
              tempHints[activeHint]?.answer || loading
            }
          >
            {recordingStarted ? 'Stop Recording' : 'Record problem'}
          </CustomButton>
          {
            tempHints[activeHint]?.answer || loading
              ? null
              : <Typography variant='body01-bold' color='neutral.clr-600'>
                  {
                    recordingStarted ? `${countdown} seconds remaining...` :
                    "You can explain only once in 30 seconds"
                  }
                </Typography>
          }
        </Box>
      </Box>
    </Drawer>
  );
};

export default Hint;
